// Core
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { 
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Input,
  Select,
  Grid,
  Text,
  useColorModeValue
} from '@chakra-ui/react';

// Assets
import { ReactComponent as ImagePlaceholder } from 'assets/svg/image-placeholder.svg';
import { apiService } from 'services/ApiService';

// Components
import { DeleteIcon, AddNewAvatarIcon } from 'components/Icons/Icons';

const UpdateAvatarModal = ({ 
  userData, setUserData,
  isOpen, onOpen, onClose 
}) => {
  const bgUpdateAvatarIcon = useColorModeValue('white', '#0E1633');
  const textBrowseColor = useColorModeValue('#262D47', '#9FABCB');
  const iconDeleteColor = useColorModeValue('#262D47', '#9FABCB');

  const [errorCard, setErrorCard] = useState();
  const [userImage, setUserImage] = useState(userData?.image);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [isDisabledButton, setIsDisabledButton] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (userData?.image) {
      setUserImage(userData?.image);
    }
  }, [userData]);

  const handleClickImage = () => {
    document.getElementById('fileInput').click(); 
  };

  const handleFileChange = (e) => {
    setIsDisabledButton(false);
    const file = e.target.files[0];
    if (!file) { return; }

    setErrorCard();

    if (file.size > 5 * 1024 * 1024) {
      setErrorCard('File is too large! Maximum size is 5 MB.');
      return;
    }

    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setErrorCard('Unsupported file type! Only JPG and PNG are allowed.');
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setUserImage(reader.result);
      //setUserData({ ...userData, image: reader.result }); 
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = () => {
    setIsDisabledButton(false);
    setSelectedFile(null);
    setUserImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; 
    }

    setErrorCard();
  };

  const handleUpdateAvatar = useCallback(async () => {
    setIsLoadingButton(true);

    if (selectedFile) {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('itemType', 'user');
      formData.append('sync', true);

      try {
        const response = await apiService.uploadImage(formData); 
        console.log('File uploaded successfully:', response.data);
        setUserData({ ...userData, image: response.data }); 
        onClose();
        setIsDisabledButton(true);
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    } else {
      if(userData?.image === null && selectedFile === null){
        setErrorCard('You haven`t selected a photo to upload.');
        setIsLoadingButton(false);
        return;
      }

      try {
        const response = await apiService.deleteImage({itemType: 'user'}); 
        console.log('File uploaded successfully:', response.data);
        setUserData({ ...userData, image: null }); 
        onClose();
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }

    setIsLoadingButton(false);
  });

  return (
  	<> 
  	<Modal isOpen={isOpen} onClose={isLoadingButton ? () => {} : () => {
        onClose();
        setErrorCard();
        setUserImage(userData?.image);
        setSelectedFile(null);
        setIsDisabledButton(true);
      }}>
      <ModalOverlay />
      <ModalContent className="modal__simple modal__simple_update-avatar" maxW={{ base: '492px' }}>
        { !isLoadingButton && 
          <ModalCloseButton />
        }

        <Text 
          variant="title-lg"
          display="flex" 
          alignItems="center" 
          mt="1px"
          justifyContent="center" 
          fontSize='22px !important'>Upload Photo</Text>
        
        <ModalBody>
          <Box 
            borderRadius="100%"
            bg="#099BFF"
            w="100%"
            h="300px"
            filter="blur(100px)"
            position="absolute"
            top="-80%"
            zIndex="-1"
          />

          { userImage ? 
            <Box>
              <Box 
                position="relative" 
                overflow="hidden" w="100%" h="260px"
                borderRadius="16px"
                mt="16px">
                <Box zIndex="1" w="100%" h="260px" bg={`url(${userImage})`} filter="blur(12px)" />
                <Image 
                  zIndex="2"
                  top="0" position="absolute"
                  src={userImage} 
                  objectFit="contain"
                  w="100%" 
                  h="260px" />

                <Button
                  variant="secondary-md" 
                  position="absolute" 
                  top="12px" 
                  zIndex="5"
                  w="48px"
                  h="48px"
                  right="12px" 
                  onClick={handleRemoveFile}
                  leftIcon={<DeleteIcon color={iconDeleteColor} mr="-0.5rem" fontSize="24px" />} />
              </Box>

              { errorCard && 
                <Text textAlign="center" variant="text-md" color="red" mt="16px" mb="0px">
                  {errorCard}
                </Text> 
              }

              <Box 
                h="44px"
                onClick={handleClickImage} 
                mt="12px"
                mb="-4px"
                cursor="pointer"
                _hover={{
                  borderColor: '#1c5175'
                }}
                borderRadius="16px"
                border="1px dashed #099bffa6"
                background={bgUpdateAvatarIcon}
                textAlign="center">
                <Flex gap="5px" alignItems="center" justifyContent="center" pt="9px">
                  <AddNewAvatarIcon fontSize="24px" />
                  <Text variant="title-md">Upload new photo</Text>
                </Flex>
                
              </Box>
            </Box>
            : 
            <Box 
              h="260px"
              onClick={handleClickImage} 
              mt="16px"
              cursor="pointer"
              _hover={{
                borderColor: '#1c5175'
              }}
              mb="8px"
              p="56px 0 28px 0"
              borderRadius="16px"
              border="1px dashed #099bffa6"
              background={bgUpdateAvatarIcon}
              textAlign="center">
              <ImagePlaceholder style={{margin: '0 auto'}} h="88px" />
              { errorCard && 
                <Text variant="text-md" color="red" mt="8px" mb="-12px">
                  {errorCard}
                </Text> 
              }
              <Text variant="text-md" color={textBrowseColor} p="18px 0 8px 0">Browse your file</Text>
              <Text variant="text-xs" color="#8794C2" pb="5px">Maximum size: <b style={{fontWeight:'500'}}>5 mb</b></Text>
              <Text variant="text-xs" color="#8794C2">Supporting Image Type: <b style={{fontWeight:'500'}}>JPG, JPEG, PNG</b></Text>
            </Box>
          }

          <input
            id="fileInput"
            type="file"
            accept=".jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={handleFileChange}
            ref={fileInputRef}
          />

	        <Grid 
            gap="12px" mt="16px" w="100%"
            templateColumns={{base: '1fr', md: '1fr 1fr'}}>
            <Button
             variant="secondary-md" 
             disabled={isLoadingButton}
             onClick={() => {
              onClose();
              setErrorCard();
              setUserImage(userData?.image);
              setSelectedFile(null);
              setIsDisabledButton(true);
             }}>Back</Button>
	          <Button 
              variant="button-md" 
              onClick={handleUpdateAvatar} 
              disabled={isDisabledButton}
              isLoading={isLoadingButton}>Confirm</Button>
	        </Grid>
        </ModalBody>
      </ModalContent>
    </Modal> 
    </>
  );
};

export default UpdateAvatarModal;
